import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import TileGrid from 'components/TileGrid';
import InfiniteScroll from 'components/InfiniteScroll';
import { Element } from 'react-scroll';
import List from 'components/List';
import Panel from 'components/Panel';
import { setHelmetTags } from 'helpers/HelmetHelper';
import SupportLink from 'components/SupportLink';
import { SEARCH_TILE } from 'components/TileV2';
import {
  isAuthorized,
  listProcessor,
  listProcessorWithAnimation,
  listItemProcessor,
  replayVideoProcessor,
  searchListVideoProcessor,
} from 'helpers/VideoProcessorHelper';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import {
  BUILD_VERSION,
  callTealiumEvent,
  setTealiumProfileId,
  TEALIUM_EVENT_PREFIX,
} from '@foxcorp/delta-web-analytics';
import { noop } from 'helpers/GlobalHelper';
import {
  isLive,
  isSportsCollection,
  getRequiresAuth,
  isTypeVideo,
  nonFoxNowSkus,
  filterMissingDeepLinkItems,
} from 'helpers/ContentHelper';
import { helperGetApiHeaders, helperConvertApiRoute } from 'helpers/ApiHelper';
import {
  getLocation,
  hasLocationCookies,
  getLocationPermission,
  GEO_LOCATION_STATUS_DENIED,
} from 'helpers/GeoLocationHelper';
import { refreshPage } from 'helpers/RouteHelper';
import { searchVideosEnabled } from 'helpers/ConfigHelper';
import {
  getOptimizelySearchVideoEnabled,
  getOptimizelyResultsOverrideEnabled,
} from 'helpers/OptimizelyHelper';
import Loader from 'components/Loader';
import Shelf from './components/Shelf';
import geoLocationStyle from './geoLocation.css';
import Style from './SearchResults.module.scss';

const INFINITE_SCROLL_DEBOUNCE = 50;
const INFINITE_SCROLL_OFFSET = 200;
class SearchResults extends Component {
  constructor(props) {
    super(props);
    this.state = {
      geoLocationError: null,
      geoLocationLoading: null,
      geoLocationDenied: null,
    };
  }

  componentDidMount() {
    const { coordinates } = this.props;
    const locationCookiesAvailable = hasLocationCookies();

    if (locationCookiesAvailable) {
      if (!coordinates) getLocation(this.getLocationCallback);
    } else {
      getLocationPermission(this.getLocationPermissionCallback);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { results, setSearchResultCount, resultsTimedOut, clearTimer } = this.props;
    const {
      results: nextResults,
      account: nextAccount,
      userInput: nextUserInput,
      isValidInput: nextIsValidInput,
      fetching,
      loading,
      resultsTimedOut: nextResultsTimedOut,
    } = nextProps;

    if (
      nextResults &&
      nextIsValidInput &&
      !_.isEqual(results, nextResults) &&
      !nextResultsTimedOut
    ) {
      const parsedResults = this.parseResults(_.get(nextResults, 'data'));
      const { resultCount } = parsedResults;
      const noResults = resultCount === 0;
      if (noResults) clearTimer();
      setSearchResultCount(resultCount);
      const action = 'search_event';
      const profileId = _.get(nextAccount, 'profileId');
      const searchResults = resultCount === 0 ? 'no search results' : resultCount;
      let data = null;
      if (noResults && !fetching && !loading) {
        data = setTealiumProfileId(profileId, {
          action,
          platform_version: BUILD_VERSION,
          search_term: nextUserInput,
          search_result_type: 'no results returned',
          num_search_results: searchResults,
          ...getCommonTealiumProps(),
        });
        callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
      }
    }
    if (nextResultsTimedOut && resultsTimedOut !== nextResultsTimedOut) {
      const action = 'search_event';
      const profileId = _.get(nextAccount, 'profileId');
      const parsedResults = this.parseResults(_.get(nextResults, 'data'));
      const { resultCount } = parsedResults;
      setSearchResultCount(resultCount);
      const searchResults = resultCount === 0 ? 'no search results' : resultCount;
      const data = setTealiumProfileId(profileId, {
        action,
        platform_version: BUILD_VERSION,
        search_term: nextUserInput,
        search_result_type: 'timer expired',
        num_search_results: searchResults,
        ...getCommonTealiumProps(),
      });
      callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
    }
  }

  getLocationCallback = (error, coordinates) => {
    const { setCoordinates } = this.props;

    if (coordinates) {
      this.setState({ geoLocationLoading: false });
      setCoordinates(coordinates);
    }

    if (error) {
      this.setState({ geoLocationError: error });
      this.setState({ geoLocationLoading: false });
    }
  };

  getLocationPermissionCallback = (error, permission) => {
    if (permission === GEO_LOCATION_STATUS_DENIED) this.setState({ geoLocationDenied: true });
  };

  filterList = item => {
    if (!item) return false;
    const { '@type': type, livePlayerScreenUrl, network } = item;

    if (isLive(type)) {
      const { isAuthenticated, preauthorizedResources } = this.props;
      const requiresAuth = getRequiresAuth({ video: item });
      const userIsAuthorized = isAuthorized({
        prioritizedNetwork: network,
        preauthorizedResources,
        isAuthenticated,
        requiresAuth,
      });
      const hideUnauthorizedVideo = isAuthenticated && !userIsAuthorized;
      const videoHasPlayableAsset = !!livePlayerScreenUrl;
      if (!videoHasPlayableAsset || hideUnauthorizedVideo) return false;
    }

    return true;
  };

  loadMore = () => {
    if (this.panel) this.panel.fetchNextPage();
  };

  parseResults = panel => {
    const { results: resultExternal } = _.get(window.ExternalConfig, 'search');
    const resultShelves = getOptimizelyResultsOverrideEnabled()
      ? window?.optimizelyResultsOverrideValues
      : resultExternal;
    const shelves = _.map(resultShelves, shelfInfo => {
      if (isTypeVideo(shelfInfo.type) && !getOptimizelySearchVideoEnabled()) {
        return null;
      }
      return this.renderShelf(shelfInfo, panel);
    });
    const filteredShelves = _.reject(shelves, _.isEmpty);
    let resultCount = 0;
    const shelfArray = _.map(filteredShelves, ({ shelf, count }) => {
      resultCount += count;
      return shelf;
    });

    return { shelfArray, resultCount };
  };

  renderFilteredItems() {
    const { accessToken, deviceId, selectedFilter, selectedTab, displayGrid } = this.props;
    if (!selectedFilter) return null;
    const selectedFilterParams = selectedFilter.onClickParams || selectedFilter;
    const selectedFilterIdx = _.get(selectedFilterParams, 'idx', 0);
    const url = _.get(selectedFilterParams, 'url');

    const panelProps = {
      accessToken,
      deviceId,
      key: 'filteredItemsPanel',
      renderer: panel => {
        const items = _.get(panel, 'member', []);
        let deepLinkFilterList = items;
        if (panel?.panelType === 'videos') {
          deepLinkFilterList = filterMissingDeepLinkItems(items, nonFoxNowSkus);
        }
        const seo = _.get(panel, 'SEO', {});
        return this.renderItems({ key: url, items: deepLinkFilterList, seo });
      },
      panelData: {},
      pageType: 'browse',
      panelId: `${url}-${selectedTab}-${selectedFilterIdx}`,
      panelUrl: url,
      propsToWatch: { displayGrid },
      hasInfiniteScroll: true,
      headerGenerator: helperGetApiHeaders,
      routeGenerator: helperConvertApiRoute,
      onRef: ref => {
        this.panel = ref;
      },
      panelPropsToWatch: [{ uid: 'isFetchingPanelData', location: 'fetching' }],
    };
    return <Panel {...panelProps} />;
  }

  renderItems(sentComponentProps) {
    const componentProps = { ...sentComponentProps };
    const {
      onResultClick,
      isAuthenticated,
      displayGrid,
      tileAnimation,
      preventLazyLoad,
      renderImage,
    } = this.props;
    let processor = listItemProcessor;
    let tileType = null;
    let tileIndexOffset = 0;
    if (tileAnimation) {
      processor = listProcessorWithAnimation;
      tileType = 'fadeAnimated';
      const count = componentProps.items.length;
      tileIndexOffset = count - (count % 25 || 25);
    } else if (displayGrid) {
      processor = listProcessor;
    }

    componentProps.gridType = componentProps.gridType || 'grid-5-4-3-1';
    componentProps.tileDetailToggle = componentProps.tileDetailToggle || false;
    componentProps.onTitleClick = componentProps.onTitleClick || onResultClick;
    componentProps.onTileClick = componentProps.onTileClick || onResultClick;
    componentProps.onLinkClick = componentProps.onLinkClick || onResultClick;
    componentProps.processor = componentProps.processor || processor;
    componentProps.disableHoverStateActionButtons = true;
    componentProps.tileType = tileType;
    componentProps.context = 'SearchResults';
    componentProps.preventLazyLoad = preventLazyLoad;
    componentProps.renderImage = componentProps.renderImage || renderImage;
    if (componentProps.tileIndexOffset === undefined) {
      componentProps.tileIndexOffset = tileIndexOffset;
    }

    componentProps.isAuthenticated = isAuthenticated;
    let content = null;
    if (!displayGrid) {
      content = <List {...componentProps} />;
    }

    content = <TileGrid tileV2Type={SEARCH_TILE} {...componentProps} />;

    return (
      <>
        {setHelmetTags(componentProps.seo)}
        {content}
      </>
    );
  }

  renderLocationDenied() {
    const { geoLocationError, geoLocationDenied } = this.state;
    if (!geoLocationError && !geoLocationDenied) return null;

    const {
      search_geoLocationErrorHeader: title,
      search_geoLocationErrorBody: body,
      search_geoLocationErrorPrompt: button,
    } = window.Language;

    return (
      <div className={geoLocationStyle.wrapper}>
        <h3 className={geoLocationStyle.title}>{title}</h3>
        <p className={geoLocationStyle.body}>{body}</p>
        <div className={geoLocationStyle.actions}>
          <button className={geoLocationStyle.button} onClick={refreshPage} type="button">
            {button}
          </button>
        </div>
        <SupportLink theme={geoLocationStyle} />
      </div>
    );
  }

  renderLocationRequest() {
    const { geoLocationError, geoLocationLoading, geoLocationDenied } = this.state;
    if (geoLocationError || geoLocationDenied) return this.renderLocationDenied();
    if (geoLocationLoading) return <Loader />;

    const {
      search_geoLocationPromptHeader: title,
      search_geoLocationPromptBody: body,
      search_geoLocationAllowButton: button,
    } = window.Language;

    const ctaFunction = () => {
      this.setState({ geoLocationLoading: true });
      getLocation(this.getLocationCallback);
    };

    return (
      <div className={geoLocationStyle.wrapper}>
        <h3 className={geoLocationStyle.title}>{title}</h3>
        <p className={geoLocationStyle.body}>{body}</p>
        <div className={geoLocationStyle.actions}>
          <button className={geoLocationStyle.button} onClick={ctaFunction} type="button">
            {button}
          </button>
        </div>
      </div>
    );
  }

  renderPanel = panel => {
    const { coordinates, panelPaginated } = this.props;
    const videos = _.get(panel, 'items.member');
    const isSportsPanel = isSportsCollection(videos);

    if (!coordinates && isSportsPanel) return this.renderLocationRequest();

    const { id: panelId } = panel;
    let itemList = _.get(panel, 'items.member');
    let deepLinkFilterList = itemList;
    if (panel?.panelType === 'videos') {
      deepLinkFilterList = filterMissingDeepLinkItems(itemList, nonFoxNowSkus);
    }
    if (panelPaginated && panelPaginated.length)
      itemList = deepLinkFilterList.concat(panelPaginated);

    return this.renderItems({ key: panelId, items: itemList });
  };

  renderPanels() {
    const { accessToken, deviceId, results, filteredItems, panels, selectedTab } = this.props;
    if (results || filteredItems || !panels || !panels.member || !panels.member.length) return null;

    return panels.member.map((panel, idx) => {
      if (idx !== selectedTab) return null;
      const panelId = panel.id;
      return (
        <Panel
          accessToken={accessToken}
          deviceId={deviceId}
          key={panelId}
          renderer={this.renderPanel}
          panelData={panel}
          headerGenerator={helperGetApiHeaders}
          routeGenerator={helperConvertApiRoute}
          pageType="browse"
          panelId={`${selectedTab}-${panelId}`}
          data-test="web-search"
          hasInfiniteScroll
          onRef={ref => {
            this.panel = ref;
          }}
          panelPropsToWatch={[{ uid: 'isFetchingPanelData', location: 'fetching' }]}
        />
      );
    });
  }

  renderResultPanel = panel => {
    const { fetching, isValidInput, loading } = this.props;
    const { search_noResults: titleNoResults } = window.Language;

    const { shelfArray, resultCount } = this.parseResults(panel);

    if (resultCount === 0 && isValidInput && !fetching && !loading) {
      return <h1 className={Style.searchResultTitle}>{titleNoResults}</h1>;
    }

    return shelfArray;
  };

  renderResults() {
    const { accessToken, deviceId, results, loading, selectedTab } = this.props;
    const panel = _.get(results, 'data', null);
    const panelId = _.get(panel, '@id', null);
    if (!panel) return null;
    return (
      <Panel
        accessToken={accessToken}
        deviceId={deviceId}
        fetching={loading}
        renderer={this.renderResultPanel}
        panelData={panel}
        headerGenerator={helperGetApiHeaders}
        routeGenerator={helperConvertApiRoute}
        pageType="browse"
        panelId={`${selectedTab}-${panelId}`}
        data-test="web-search"
        hasInfiniteScroll
        onRef={ref => {
          this.panel = ref;
        }}
        panelPropsToWatch={[{ uid: 'isFetchingPanelData', location: 'fetching' }]}
      />
    );
  }

  togglePanel = dataLocation => {
    const { openToggle } = this.state;
    const newToggle = {
      ...openToggle,
    };
    newToggle[dataLocation] = !openToggle[dataLocation];
    this.setState({ openToggle: newToggle });
  };

  renderShelf = (shelfInfo, panel) => {
    const { display, heading, dataLocation, type } = shelfInfo;
    if (!display) return null;
    const headlineText = _.get(window.Language, heading);
    let list = _.get(panel, `types.${dataLocation}.member`, []);
    if (dataLocation === 'tubi') {
      list = panel?.types?.['tubi'];
    }
    let deepLinkFilterList = list;
    if (panel?.panelType === 'videos') {
      deepLinkFilterList = filterMissingDeepLinkItems(list, nonFoxNowSkus);
    }
    const filteredList = _.filter(deepLinkFilterList, this.filterList);
    const listLength = filteredList.length;
    const shelfItemNumberLimit = 4;
    const seeMoreText = `See More ${
      listLength > shelfItemNumberLimit ? `(${listLength - shelfItemNumberLimit})` : ''
    }`;
    let processor = searchVideosEnabled() && isTypeVideo(type) ? searchListVideoProcessor : null;
    if (dataLocation.includes('sportingEvent')) processor = replayVideoProcessor;
    const shelfProps = {
      headlineText,
      theme: Style,
      items: filteredList,
      seeMoreText,
      renderItems: this.renderItems.bind(this),
      processor,
      itemNumberLimit: shelfItemNumberLimit,
    };
    const shelf = listLength ? <Shelf {...shelfProps} /> : null;

    return { shelf, count: listLength };
  };

  render() {
    const {
      loading,
      input,
      fetching,
      selectedFilter,
      selectedTab,
      isValidInput,
      localScrollContainer,
    } = this.props;

    const infiniteScrollProps = {
      debounce: INFINITE_SCROLL_DEBOUNCE,
      offset: INFINITE_SCROLL_OFFSET,
      bottomHandler: this.loadMore,
    };
    const classname = input ? classNames(Style.search, Style.results) : Style.search;
    const results = this.renderResults();
    const filteredItems = (
      <InfiniteScroll {...infiniteScrollProps}>
        <Element name="searchResultsInfiniteScroll">{this.renderFilteredItems()}</Element>
      </InfiniteScroll>
    );
    const panels = this.renderPanels();
    const content = isValidInput ? results : [results, filteredItems, panels];
    const key = `search-results-${selectedTab}-${selectedFilter}`;
    const containerId = localScrollContainer ? 'searchResultsScrollContainer' : null;
    const isLoading = fetching || loading;

    return (
      <div key={key} className={classname} id={containerId}>
        {content}
        {isLoading ? <Loader /> : null}
      </div>
    );
  }
}

SearchResults.defaultProps = {
  accessToken: null,
  account: {},
  clearTimer: noop,
  coordinates: null,
  deviceId: null,
  fetching: false,
  filteredItems: [],
  input: null,
  isAuthenticated: false,
  loader: null,
  loading: false,
  onResultClick: null,
  panelPaginated: [],
  panels: {},
  preauthorizedResources: [],
  renderImage: noop,
  results: {},
  resultsTimedOut: false,
  selectedFilter: null,
  selectedTab: null,
  setCoordinates: noop,
  setSearchResultCount: noop,
  userInput: {},
};

SearchResults.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  clearTimer: PropTypes.func,
  coordinates: PropTypes.shape({}),
  deviceId: PropTypes.string,
  displayGrid: PropTypes.bool.isRequired,
  fetching: PropTypes.bool,
  filteredItems: PropTypes.arrayOf(PropTypes.shape({})),
  input: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  isValidInput: PropTypes.bool.isRequired,
  loader: PropTypes.func,
  loading: PropTypes.bool,
  localScrollContainer: PropTypes.bool.isRequired,
  onResultClick: PropTypes.func,
  panelPaginated: PropTypes.arrayOf(PropTypes.shape({})),
  panels: PropTypes.shape({}),
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  preventLazyLoad: PropTypes.bool.isRequired,
  renderImage: PropTypes.func,
  results: PropTypes.shape({}),
  resultsTimedOut: PropTypes.bool,
  selectedFilter: PropTypes.shape({}),
  selectedTab: PropTypes.number,
  setCoordinates: PropTypes.func,
  setSearchResultCount: PropTypes.func,
  tileAnimation: PropTypes.bool.isRequired,
  userInput: PropTypes.string,
};

export default SearchResults;
